// @flow
import React from 'react'
import clsx from 'clsx'

import ButtonM from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import { makeStyles } from '@material-ui/styles'

import { useGlobalState } from '../store'

import type { GlobalState } from '../types/globalState'

type Props = {
  children?: any,
  title?: string,
  subTitle?: string,
  color?: string,
  variant?: string,
  size?: string,
  onClick?: Function,
  style?: Object,
  disabled?: boolean,
  loading?: boolean,
  select?: boolean,
  testId?: string,
  toUpperCase?: boolean,
  className?: string,
}

const useCustomStyle = makeStyles({
  buttonDecline: {
    borderColor: '#D5DEE6',
    backgroundColor: '#F5F6FA',
    border: '1px',
    borderStyle: 'solid',
    color: '#44546A',

    '&:hover': {
      backgroundColor: '#D5DEE6',
    },
  },
  buttonAccept: {
    color: '#ffffff',
    borderColor: '#2E7E19',
    border: '1px',
    borderStyle: 'solid',
    backgroundColor: '#43BC23',
    fontWeight: 'bold',

    '&:hover': {
      backgroundColor: '#2E7E19',
    },
  },
  buttonReject: {
    color: '#ffffff',
    borderColor: '#7e1919',
    backgroundColor: '#bc2323',
    fontWeight: 'bold',

    '&:hover': {
      backgroundColor: '#7e1928',
    },
  },
})

const nativeColors = ['primary', 'secondary']

/**
 *
 * @param color si la couleur est autre que les couleur par défaut de Material UI, utilise les styles locaux du Button
 * @param variant
 * @param size
 * @param subTitle
 * @param children
 * @param disabled
 * @param loading
 * @param title
 * @param onClick
 * @param style
 * @param testId
 * @param toUpperCase
 * @param className
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */

const Button = ({
  color = 'primary',
  variant,
  size = 'medium',
  subTitle = '',
  children = '',
  disabled = false,
  loading = false,
  title = '',
  onClick = (noop) => noop,
  style,
  testId = 'buttonTester',
  toUpperCase = false,
  className,
  ...props
}: Props) => {
  const handleClick = () => {
    onClick && onClick()
  }

  const [theme]: [GlobalState] = useGlobalState('theme')
  const classes = useCustomStyle({ theme })

  const loadCustomColorsButtons = (color) => {
    switch (color) {
      case 'accept':
        return clsx(classes.buttonAccept, className)
      case 'decline':
        return clsx(classes.buttonDecline, className)
      case 'reject':
        return clsx(classes.buttonReject, className)
      default:
        return className
    }
  }

  return (
    <ButtonM
      {...props}
      style={style}
      color={nativeColors.includes(color) ? color : 'default'}
      variant={variant}
      size={size}
      disabled={disabled || loading}
      onClick={handleClick}
      data-testid={testId}
      className={loadCustomColorsButtons(color)}
    >
      {loading ? (
        <CircularProgress size={20} style={{ color: theme.colors.grey }} />
      ) : (
        <>
          {!!title && <div>{toUpperCase ? title.toUpperCase() : title}</div>}
          {!!subTitle && <div>{subTitle}</div>}
          {toUpperCase ? children.toUpperCase() : children}
        </>
      )}
    </ButtonM>
  )
}

export default Button
